import { Alert, Box, Link } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { useEffect, useMemo, useRef } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import {
  useCurrentSection,
  useQuestionnaireInitialization,
} from "../../features/questionnaire";
import type { WithMetadata } from "../../pages";
import { type FlattenedSectionProps, Section } from "./Section";
import { QuestionnaireForm } from "./QuestionnaireForm";
import { Preamble } from "./Amble";

import { useEnvironmentValues } from "../../hooks/environment/useEnvironment";
import { useFocusFirstElement } from "../../hooks/useFocusFirstElement";
import { useLocale } from "../../hooks/useLocale";
import { useQuestionnaireActions } from "../../features/questionnaire";
import { useQuestionRefs } from "../../hooks/useQuestionRefs/useQuestionRefs";
import "./Questionnaire.css";

import { useSetSeeMoreCoversState } from "../../hooks/useCoverGeniusSeeMoreCoversState";
import { useSectionNavigation } from "../../hooks/useSectionNavigation";
import { useApiErrorValue } from "../../hooks/useApiError";
import type { QuestionnaireProps } from ".";
import { useCoverGeniusSection } from "../../hooks/useCoverGeniusSection";

/**
 * Standard questionnaire implementation with section-by-section navigation.
 * Shows one section at a time with slide transitions.
 */
export const Questionnaire = ({
  ref,
  ...props
}: WithMetadata<QuestionnaireProps>) => {
  const {
    elementType: Element = "div",
    config,
    sections,
    validations,
    componentName,
    actions,
    currentSection: initialCurrentSection,
    pageId,
    variant,
    preamble,
    preambleName,
    ...otherProps
  } = props;
  const { locale } = useLocale();
  const { site } = useEnvironmentValues();
  const { setCurrentSectionIndex } = useQuestionnaireActions();
  const { focusFirstError } = useQuestionRefs();
  const apiError = useApiErrorValue();

  // Include locale in config object
  const configWithLocale = useMemo(
    () => ({
      ...config,
      locale,
    }),
    [config, locale],
  );
  useSetSeeMoreCoversState(false);
  useQuestionnaireInitialization(sections, validations, configWithLocale);
  const {
    currentSection: currentSectionFromState,
    currentSectionIndex,
    errors: currentSectionErrors,
  } = useCurrentSection(sections);
  const currentSection = currentSectionFromState as FlattenedSectionProps;
  const prevSectionIndexRef = useRef(currentSectionIndex);
  const sectionRef = useRef<HTMLElement>(null);
  const { focusFirstElement } = useFocusFirstElement(sectionRef);
  const isSlideBack = currentSectionIndex < prevSectionIndexRef.current;

  const submitActionLabel = useMemo(() => {
    const submitAction = actions?.find(
      action => action.actionType === "submit",
    );
    return submitAction?.label || "Continue to quotes";
  }, [actions]);
  // Used for sliding animation
  useEffect(() => {
    prevSectionIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  useCoverGeniusSection({
    allQuestions: currentSection.questions,
  });

  // Update section URL on initialization and section change
  useSectionNavigation(
    currentSectionIndex,
    setCurrentSectionIndex,
    initialCurrentSection,
    sections,
  );

  const classes = classNames(
    "mobius",
    "mobius-questionnaire",
    otherProps.className,
    {
      "mobius-questionnaire--slide-next": !isSlideBack,
      "mobius-questionnaire--slide-back": isSlideBack,
    },
  );

  return (
    <Element
      ref={ref}
      {...otherProps}
      {...(componentName ? { "data-component": componentName } : {})}
      {...(variant ? { "data-variant": variant } : {})}
      className={classes}
    >
      {apiError && (
        <Box paddingBlockEnd="sm">
          <Alert
            variant="error"
            header="Please reload the page"
            className="mobius-questionnaire__error-alert"
          >
            We hit a snag on our end. Please{" "}
            <Link
              href="#"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                window.location.reload();
              }}
            >
              reload this page
            </Link>{" "}
            to continue. We&apos;ve saved your answers.
          </Alert>
        </Box>
      )}
      {currentSectionIndex === 0 && (
        <Preamble
          amble={preamble}
          ambleName={preambleName}
          site={site}
          locale={locale}
        />
      )}
      <QuestionnaireForm
        submitActionLabel={submitActionLabel}
        sections={sections}
        questionnaireVariant={variant}
      >
        <SwitchTransition>
          <CSSTransition
            key={currentSectionIndex}
            nodeRef={sectionRef}
            timeout={200}
            onEntered={() => {
              const hasErrors =
                currentSectionErrors &&
                Object.keys(currentSectionErrors).length > 0;

              if (hasErrors) {
                // Section has errors - scroll to and focus first error
                focusFirstError(currentSectionErrors);
              } else {
                // No errors - normal behavior: scroll to top and focus first element
                window?.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
                focusFirstElement();
              }
            }}
            classNames="mobius-section__transition"
          >
            <Section
              ref={sectionRef}
              {...currentSection}
              submitActionLabel={submitActionLabel}
            />
          </CSSTransition>
        </SwitchTransition>
      </QuestionnaireForm>
    </Element>
  );
};

Questionnaire.displayName = "Questionnaire";
